import React, { useState, useRef } from 'react';
import { Download, FileVideo, Book, FileText, CheckCircle2, X, Save, Check } from 'lucide-react';
import { IoCheckmarkCircle } from 'react-icons/io5';
import useContentManagementStore from '@/stores/AdminStore/ContentManagementStore';
import { LoadingSpinner } from '@/Components/common';
import './UploadTab.css';

// Icon mapping based on content type
const getIconByType = (type: 'VIDEO' | 'SCORM' | 'PDF' | 'LINK'): React.ReactNode => {
  const iconMap = {
    VIDEO: <FileVideo size={20} />,
    SCORM: <Book size={20} />,
    PDF: <FileText size={20} />,
    LINK: <FileText size={20} />
  };
  return iconMap[type];
};

const UploadTab: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);

  const {
    recentUploads,
    categories,
    loading,
  } = useContentManagementStore();

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCategoryClick = () => {
    setShowCategoryModal(!showCategoryModal);
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setShowCategoryModal(false);
    setIsAddingCustom(false);
  };

  const handleAddCustomClick = () => {
    setIsAddingCustom(true);
  };

  const handleSaveCustomCategory = () => {
    if (newCategoryName.trim()) {
      setSelectedCategory(newCategoryName.trim());
      setNewCategoryName('');
      setIsAddingCustom(false);
      setShowCategoryModal(false);
    }
  };

  const handleCancelCustom = () => {
    setNewCategoryName('');
    setIsAddingCustom(false);
  };

  return (
    <div className="upload-container">
      <div className="upload-main-section">
        {/* Upload Content File Section */}
        <div className="upload-section-box">
          <h3 className="upload-section-title">Upload Content File</h3>
          <div className="upload-area">
            <Download size={48} className="upload-icon" />
            <p className="upload-text">Drag and drop your file here</p>
            <p className="upload-subtext">Supports SCORM, AICC, MP4, PDF, DOCX (Max 500MB)</p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".scorm,.zip,.mp4,.pdf,.docx,.pptx"
            />
            <button className="upload-btn-browse" onClick={handleBrowseClick}>
              Browse Files
            </button>
            {selectedFile && (
              <div className="upload-selected-file">
                <FileText size={16} />
                <span>{selectedFile.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* External Link Section */}
        <div className="upload-section-box">
          <h4 className="upload-section-subtitle">Or Add External Content Link</h4>
          <div className="upload-input-group">
            <label>Content URL</label>
            <input
              type="text"
              placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
              className="upload-input-field"
            />
            <p className="upload-input-hint">Supports YouTube, Vimeo, and other video platforms</p>
          </div>
        </div>

        {/* Content Information Section */}
        <div className="upload-section-box">
          <h4 className="upload-section-subtitle">Content Information</h4>
          <div className="upload-input-group">
            <label>Title *</label>
            <input type="text" placeholder="Enter content title" className="upload-input-field" />
          </div>
          <div className="upload-input-group">
            <label>Description *</label>
            <textarea
              placeholder="Provide a detailed description of the content"
              className="upload-textarea-field"
              rows={4}
            />
          </div>
          <div className="upload-input-row">
            <div className="upload-input-group">
              <label>Language</label>
              <select className="upload-select-field">
                <option>Select language</option>
                <option>English</option>
                <option>Spanish</option>
              </select>
            </div>
            <div className="upload-input-group">
              <label>Category</label>
              <div ref={categoryRef} style={{ position: 'relative' }}>
                <div
                  className="upload-select-field upload-category-trigger"
                  onClick={handleCategoryClick}
                >
                  {selectedCategory || 'Select category'}
                </div>

                {showCategoryModal && (
                  <div className="category-modal">
                    {isAddingCustom ? (
                      <div className="category-add-custom-input">
                        <input
                          type="text"
                          placeholder="New category name"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="custom-category-input"
                          autoFocus
                        />
                        <button
                          className="custom-category-save-btn"
                          onClick={handleSaveCustomCategory}
                        >
                          <Check size={18} />
                        </button>
                        <button
                          className="custom-category-cancel-btn"
                          onClick={handleCancelCustom}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div
                          className="category-add-custom"
                          onClick={handleAddCustomClick}
                        >
                          + Add Custom
                        </div>
                        {categories && categories.map((category) => (
                          <div
                            key={category.id}
                            className={`category-option ${selectedCategory === category.name ? 'selected' : ''}`}
                            onClick={() => handleCategorySelect(category.name)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategory === category.name}
                              readOnly
                              className="category-checkbox"
                            />
                            <span>{category.name}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="upload-input-group">
            <label>Tags</label>
            <input
              type="text"
              placeholder="Add tags (comma separated)"
              className="upload-input-field"
            />
            <p className="upload-input-hint">e.g., leadership, management, fundamentals</p>
          </div>
          <div className="upload-input-group">
            <label>Owner</label>
            <input type="text" value="Admin User" className="upload-input-field" readOnly />
          </div>
        </div>
      </div>

      <div className="upload-sidebar">
        <div className="upload-guidelines-box">
          <h4>Upload Guidelines</h4>
          <ul className="upload-guidelines-list">
            <li><IoCheckmarkCircle className="upload-check-icon" /> Maximum file size: 500MB</li>
            <li><IoCheckmarkCircle className="upload-check-icon" /> Supported: SCORM 1.2/2004, AICC</li>
            <li><IoCheckmarkCircle className="upload-check-icon" /> Video: MP4, WebM (H.264)</li>
            <li><IoCheckmarkCircle className="upload-check-icon" /> Documents: PDF, DOCX, PPTX</li>
            <li><IoCheckmarkCircle className="upload-check-icon" /> All fields marked * are required</li>
          </ul>
        </div>

        <div className="upload-actions-box">
          <h4>Actions</h4>
          <button className="upload-btn-publish">
            <CheckCircle2 size={16} /> Publish Content
          </button>
          <button className="upload-btn-draft">
            <Save size={16} /> Save as Draft
          </button>
          <button className="upload-btn-cancel">
            <X size={16} /> Cancel
          </button>
        </div>

        <div className="upload-recent-uploads-box">
          <h4>Recent Uploads</h4>
          {loading && !recentUploads ? (
            <LoadingSpinner size="sm" />
          ) : (
            recentUploads && recentUploads.map((upload) => (
              <div key={upload.id} className="upload-recent-item">
                {getIconByType(upload.type)}
                <div>
                  <p className="upload-recent-title">{upload.title}</p>
                  <p className="upload-recent-date">{upload.uploadedDate}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadTab;
